import * as Sentry from '@sentry/browser';
import { LitElement, html } from 'lit';
import { ContextConsumer } from '@lit/context';
import { authContext } from '../services/context/auth-context.js';
import { NotificationBus } from '../services/notification-bus.js';
import {
    fetchQuote,
    acceptQuote,
    submitPOReference,
    submitCustRefRequired,
    mapApiStatus,
    ContactNotAuthorizedError,
} from '../services/apis/quote-api-service.js';
import '../components/quote/quote-header.js';
import '../components/quote/quote-class-section.js';
import '../components/quote/quote-financial-summary.js';
import '../components/quote/quote-acceptance-section.js';
import '../components/quote-status/quote-pending.js';
import '../components/quote-status/quote-pending-acceptance.js';
import '../components/quote-status/quote-pending-payment.js';
import '../components/quote-status/quote-confirmed.js';
import '../components/quote-status/quote-error.js';
import '../components/quote-status/quote-not-found.js';
import '../components/quote-status/quote-not-authorized.js';
import '../components/icons/info-icon.js';
import '../components/icons/warning-icon.js';

// Quote status constants - internal app values (mapped from API statuses)
const QUOTE_STATUS = Object.freeze({
    PENDING: 'pending',
    PENDING_ACCEPTANCE: 'pending-acceptance',
    PENDING_PAYMENT: 'pending-payment',
    CONFIRMED: 'confirmed',
    CONFIRMED_CHARGE_ACCOUNT: 'confirmed-charge-account',
    NOT_AUTHORIZED: 'not-authorized',
    // Non-data related statuses for unified rendering
    ERROR: 'error',
    NOT_FOUND: 'not-found',
});

class QuoteDetailsPage extends LitElement {
    static properties = {
        quoteReference: { type: String },
        quoteData: { type: Object, state: true },
        loading: { type: Boolean, state: true },
        error: { type: String, state: true },
        authState: { type: Object, state: true },
    };

    // Use Light DOM for Tailwind compatibility
    createRenderRoot() {
        return this;
    }

    constructor() {
        super();
        this.quoteReference = '';
        this.quoteData = null;
        this.loading = false;
        this.error = null;
        this.authState = null;
        this._isInitialLoad = true;
    }

    connectedCallback() {
        super.connectedCallback();
        // Subscribe to auth context
        this._authConsumer = new ContextConsumer(this, {
            context: authContext,
            subscribe: true,
            callback: (value) => {
                this.authState = value;
            },
        });

        // Listen for reload events from child components (e.g., print upsell)
        this._boundReloadHandler = this._handleReloadQuote.bind(this);
        this.addEventListener('reload-quote', this._boundReloadHandler);

        // Load quote data when component is connected
        this.loadQuoteData();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        if (this._boundReloadHandler) {
            this.removeEventListener('reload-quote', this._boundReloadHandler);
        }
    }

    /**
     * Lit lifecycle: Called when component properties change
     * Handles quote reference changes from URL updates
     */
    updated(changedProperties) {
        super.updated(changedProperties);

        // Reload data only when quoteReference changes
        if (changedProperties.has('quoteReference')) {
            const oldRef = changedProperties.get('quoteReference');
            const newRef = this.quoteReference;

            if (oldRef !== newRef && newRef && !this._isInitialLoad) {
                this.error = null;
                this.loadQuoteData();
            }

            if (this._isInitialLoad) {
                this._isInitialLoad = false;
            }
        }
    }

    async loadQuoteData() {
        this.loading = true;
        this.error = null;

        try {
            const data = await fetchQuote(this.quoteReference);

            if (!data) {
                // Quote not found (404 from API)
                this.quoteData = { status: QUOTE_STATUS.NOT_FOUND };
                this.loading = false;
                return;
            }

            if (data._notAuthorized) {
                // Contact not authorized for this quote (403 from API)
                this.quoteData = { status: QUOTE_STATUS.NOT_AUTHORIZED };
                this.loading = false;
                return;
            }

            const internalStatus = mapApiStatus(data.quoteStatus, data.isMUF);
            data.status = internalStatus;

            this.quoteData = data;

            // Set quote context in Sentry
            Sentry.setContext('quote', {
                reference: this.quoteReference,
                customer: this.quoteData.custName,
                contactEmail: this.quoteData.contactEmail,
                total: this.quoteData.total,
                status: this.quoteData.status,
                apiStatus: this.quoteData.quoteStatus,
            });

            // Track when API returns an unrecognised status
            if (internalStatus === QUOTE_STATUS.ERROR) {
                Sentry.setContext('quote_raw', {
                    reference: this.quoteReference,
                    apiStatus: data.quoteStatus,
                    isMUF: data.isMUF,
                });
            }
        } catch (err) {
            console.error('Error loading quote data:', err);

            Sentry.captureException(err, {
                tags: {
                    feature: 'quote-loading',
                    quote_reference: this.quoteReference,
                    http_status: String(err.httpStatus || 'unknown'),
                },
                extra: {
                    response_body: err.responseBody || null,
                },
            });

            this.quoteData = { status: QUOTE_STATUS.ERROR };

            NotificationBus.show(
                html`Unable to load quote <strong>${this.quoteReference}</strong>. Please try again
                    later, or contact support if the problem persists.`,
                'warning',
                5000,
                'QuoteLoader'
            );

            if (this.quoteReference) {
                Sentry.setContext('quote', {
                    reference: this.quoteReference,
                });
            }
        } finally {
            this.loading = false;
        }
    }

    renderLoading() {
        return html`
            <div class="flex items-center justify-center min-h-[60vh]">
                <div class="text-center">
                    <span class="loading loading-spinner loading-lg text-peppermint"></span>
                    <p class="mt-4 text-gray-600">Loading quote details...</p>
                </div>
            </div>
        `;
    }

    /**
     * Navigate back to landing page with invalid reference preserved
     */
    navigateToLanding() {
        window.location.hash = `/#?ref=${encodeURIComponent(this.quoteReference)}`;
    }

    /**
     * Handle quote acceptance - PUT to API then reload
     */
    async _handleQuoteAccept() {
        this.loading = true;

        try {
            const isChargeAccount = this.quoteData?.isMUF === 'N';

            await acceptQuote(this.quoteReference);

            const successMessage = isChargeAccount
                ? 'Quote accepted! Your confirmation details are ready.'
                : 'Quote accepted! Proceeding to payment.';

            // Reload quote to get updated status from API
            await this.loadQuoteData();

            NotificationBus.show(successMessage, 'success', 3000, 'QuoteAcceptance');

            Sentry.setContext('quote', {
                reference: this.quoteReference,
                status: this.quoteData?.status,
                isMUF: this.quoteData?.isMUF,
            });
        } catch (err) {
            if (err instanceof ContactNotAuthorizedError) {
                this.quoteData = { status: QUOTE_STATUS.NOT_AUTHORIZED };
                return;
            }
            console.error('Error accepting quote:', err);
            // Liberty refuses acceptance for booking problems a retry cannot fix
            // (e.g. a distribution date in an unavailable window), so point the
            // customer at a person as well as offering a retry.
            NotificationBus.show(
                "We couldn't accept your quote. Please try again, and if it keeps failing, contact your REACH account manager.",
                'error',
                10000,
                'QuoteAcceptance'
            );
            Sentry.captureException(err);

            // Reload to ensure UI reflects server-side state
            await this.loadQuoteData();
        }
    }

    /**
     * Handle PO reference submission
     */
    async _handleSubmitPO(e) {
        const { custRef } = e.detail;

        try {
            await submitPOReference(this.quoteReference, custRef);

            await this.loadQuoteData();

            NotificationBus.show('Purchase order reference saved.', 'success', 3000, 'POReference');
        } catch (err) {
            if (err instanceof ContactNotAuthorizedError) {
                this.quoteData = { status: QUOTE_STATUS.NOT_AUTHORIZED };
                return;
            }
            console.error('Error submitting PO reference:', err);
            NotificationBus.show(
                html`Failed to save PO reference. Please try again, or
                    <a
                        href="mailto:info@reach.nz?subject=Quote%20${encodeURIComponent(
                            this.quoteReference
                        )}%20-%20PO%20Reference"
                        class="underline font-medium"
                        >contact us</a
                    >
                    if the problem persists.`,
                'error',
                5000,
                'POReference'
            );
            Sentry.captureException(err);

            // Reload to reset child component state
            await this.loadQuoteData();
        }
    }

    /**
     * Handle PO requirement choice (Y/N) from confirmation page
     */
    async _handleSubmitPoRequired(e) {
        const { custRefRequired } = e.detail;

        try {
            await submitCustRefRequired(this.quoteReference, custRefRequired);

            await this.loadQuoteData();

            NotificationBus.show(
                custRefRequired === 'Y'
                    ? 'PO requirement noted. You can add your PO number below.'
                    : 'No PO required for this booking.',
                'success',
                3000,
                'PORequirement'
            );
        } catch (err) {
            if (err instanceof ContactNotAuthorizedError) {
                this.quoteData = { status: QUOTE_STATUS.NOT_AUTHORIZED };
                return;
            }
            console.error('Error submitting PO requirement:', err);
            NotificationBus.show(
                'Failed to save PO requirement. Please try again.',
                'error',
                5000,
                'PORequirement'
            );
            Sentry.captureException(err);

            await this.loadQuoteData();
        }
    }

    /**
     * Handle reload-quote event from child components (e.g., print upsell)
     */
    async _handleReloadQuote() {
        if (import.meta.env.DEV) {
            console.log('[QuoteDetails] Reloading quote data...');
        }

        await this.loadQuoteData();
    }

    /**
     * Status-to-view mapping for rendering different quote states
     */
    get _statusViewMap() {
        return {
            [QUOTE_STATUS.PENDING]: () => html`
                <quote-pending
                    .quoteReference="${this.quoteReference}"
                    @retry-fetch="${this.loadQuoteData}"
                >
                </quote-pending>
            `,
            [QUOTE_STATUS.PENDING_ACCEPTANCE]: () => html`
                <quote-pending-acceptance
                    .quoteReference="${this.quoteReference}"
                    .quoteData="${this.quoteData}"
                    @quote-accept="${this._handleQuoteAccept}"
                >
                </quote-pending-acceptance>
            `,
            [QUOTE_STATUS.PENDING_PAYMENT]: () => html`
                <quote-pending-payment
                    .quoteReference="${this.quoteReference}"
                    .quoteData="${this.quoteData}"
                    @submit-po="${this._handleSubmitPO}"
                >
                </quote-pending-payment>
            `,
            [QUOTE_STATUS.CONFIRMED]: () => html`
                <quote-confirmed
                    .quoteReference="${this.quoteReference}"
                    .quoteData="${this.quoteData}"
                    @submit-po="${this._handleSubmitPO}"
                    @submit-po-required="${this._handleSubmitPoRequired}"
                ></quote-confirmed>
            `,
            [QUOTE_STATUS.CONFIRMED_CHARGE_ACCOUNT]: () => html`
                <quote-confirmed
                    .quoteReference="${this.quoteReference}"
                    .quoteData="${this.quoteData}"
                    @submit-po="${this._handleSubmitPO}"
                    @submit-po-required="${this._handleSubmitPoRequired}"
                ></quote-confirmed>
            `,
            [QUOTE_STATUS.NOT_AUTHORIZED]: () => html`
                <quote-not-authorized
                    .quoteReference="${this.quoteReference}"
                    .userEmail="${this.authState?.user?.email || ''}"
                ></quote-not-authorized>
            `,
            [QUOTE_STATUS.ERROR]: () => html`
                <quote-error
                    .quoteReference="${this.quoteReference}"
                    @retry-fetch="${this.loadQuoteData}"
                >
                </quote-error>
            `,
            [QUOTE_STATUS.NOT_FOUND]: () => html`
                <quote-not-found .quoteReference="${this.quoteReference}"></quote-not-found>
            `,
        };
    }

    renderByStatus() {
        const status = this.quoteData?.status || QUOTE_STATUS.PENDING_ACCEPTANCE;
        const renderView = this._statusViewMap[status] || this._statusViewMap[QUOTE_STATUS.ERROR];
        return renderView();
    }

    render() {
        if (this.loading) {
            return this.renderLoading();
        }

        if (!this.quoteData) {
            return this.renderLoading();
        }

        return this.renderByStatus();
    }
}

customElements.define('quote-details-page', QuoteDetailsPage);
